import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import {
  TicketCheck,
  Plus,
  Clock,
  X,
  Send,
  CircleDashed,
  RefreshCw,
  CheckCircle2,
  Lock,
  MessageSquareOff,
  Eye,
} from 'lucide-react';
import { PageHeader, PageLoader } from '@repo/ui';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

const STATUS_CONFIG: Record<
  string,
  {
    bg: string;
    text: string;
    icon: any;
    accent: string;
  }
> = {
  open: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-600 dark:text-amber-400',
    icon: CircleDashed,
    accent: 'bg-amber-500',
  },
  'in-progress': {
    bg: 'bg-blue-500/10',
    text: 'text-blue-600 dark:text-blue-400',
    icon: RefreshCw,
    accent: 'bg-blue-500',
  },
  resolved: {
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-600 dark:text-emerald-400',
    icon: CheckCircle2,
    accent: 'bg-emerald-500',
  },
  closed: {
    bg: 'bg-slate-500/10',
    text: 'text-slate-600 dark:text-slate-400',
    icon: Lock,
    accent: 'bg-slate-500',
  },
};

const TicketsPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<any>(null);

  const { data: tickets, isLoading } = useQuery({
    queryKey: ['student-tickets'],
    queryFn: () => api.get('/student/tickets').then((res) => res.data?.data),
  });

  const mutation = useMutation({
    mutationFn: (data: { subject: string; description: string }) =>
      api.post('/student/tickets', data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['student-tickets'] });
      closeModal();
    },
  });

  const closeModal = () => {
    setIsModalOpen(false);
    setSubject('');
    setDescription('');
    setValidationError('');
    mutation.reset();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    const s = subject.trim();
    const d = description.trim();

    if (s.length < 2) {
      setValidationError(
        t('student.tickets.subjectMin') || 'Subject must be at least 2 characters'
      );
      return;
    }
    if (d.length < 5) {
      setValidationError(
        t('student.tickets.descMin') || 'Description must be at least 5 characters'
      );
      return;
    }

    mutation.mutate({ subject: s, description: d });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('student.tickets.title')}
        subtitle={t('student.tickets.subtitle')}
        icon={TicketCheck}
        iconClassName="text-primary"
        iconContainerClassName="bg-primary/10 border-primary/20"
      >
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary text-primary-foreground text-sm font-black hover:bg-primary/90 transition-all shadow-xl shadow-primary/20 active:scale-95"
        >
          <Plus className="h-4 w-4" />
          {t('student.tickets.create')}
        </button>
      </PageHeader>

      {isLoading ? (
        <PageLoader message={t('common.loading')} className="min-h-80" />
      ) : !tickets?.length ? (
        <div className="flex flex-col items-center justify-center py-24 px-4 text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="h-24 w-24 rounded-full bg-muted/50 flex items-center justify-center mb-6">
            <MessageSquareOff className="h-12 w-12 text-muted-foreground/40" />
          </div>
          <h3 className="text-xl font-bold text-foreground mb-2">{t('student.tickets.empty')}</h3>
          <p className="text-sm text-muted-foreground max-w-[280px]">
            {t('student.tickets.subtitle')}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {tickets.map((ticket: any) => {
            const config = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.closed;
            const StatusIcon = config.icon;

            return (
              <div
                key={ticket._id}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 hover:-translate-y-1"
              >
                {/* Status Accent Bar */}
                <div
                  className={`absolute top-0 bottom-0 w-1 transition-all group-hover:w-1.5 ${config.accent} ltr:left-0 rtl:right-0`}
                />

                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${config.bg} ${config.text}`}
                        >
                          <StatusIcon
                            className={`h-3.5 w-3.5 ${ticket.status === 'in-progress' ? 'animate-spin-slow' : ''}`}
                          />
                          {t(`student.tickets.statuses.${ticket.status}`)}
                        </span>
                      </div>

                      <h3 className="text-lg font-black tracking-tight text-foreground truncate group-hover:text-primary transition-colors">
                        {ticket.subject}
                      </h3>
                      <p className="text-sm text-muted-foreground/80 leading-relaxed line-clamp-2">
                        {ticket.description}
                      </p>
                    </div>

                    <div className="flex flex-col items-end justify-between gap-4 shrink-0 sm:h-full">
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 text-xs font-semibold text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        {format(new Date(ticket.createdAt), 'PP p', {
                          locale: t('common.lang') === 'ar' ? ar : undefined,
                        })}
                      </div>

                      <button
                        onClick={() => setSelectedTicket(ticket)}
                        className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 rounded-xl bg-primary/10 text-primary font-bold hover:bg-primary/20 transition-all text-sm sm:text-xs"
                      >
                        <Eye className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                        {t('common.viewDetails')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Ticket Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 animate-in fade-in duration-300"
            onClick={closeModal}
          />

          <div className="relative w-full max-w-lg rounded-2xl border border-border bg-card shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-start justify-between p-6 pb-0">
              <div className="space-y-1 pr-6 rtl:pr-0 rtl:pl-6">
                <h2 className="text-xl font-bold">{t('student.tickets.createTitle')}</h2>
                <p className="text-sm text-muted-foreground">{t('student.tickets.subtitle')}</p>
              </div>
              <button
                onClick={closeModal}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="ticket-subject">
                  {t('student.tickets.subject')}
                </label>
                <input
                  id="ticket-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t('student.tickets.subjectPlaceholder') as string}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow"
                  maxLength={200}
                  autoFocus
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="ticket-description">
                  {t('student.tickets.description')}
                </label>
                <textarea
                  id="ticket-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('student.tickets.descPlaceholder') as string}
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-sm text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow"
                  maxLength={2000}
                  required
                />
                <p className="text-xs text-muted-foreground text-end" dir="ltr">
                  {description.length} / 2000
                </p>
              </div>

              {(mutation.isError || validationError) && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm font-medium animate-in shake duration-300">
                  {validationError || t('student.tickets.error')}
                </div>
              )}

              <div className="flex items-center gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={mutation.isPending || !subject.trim() || !description.trim()}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mutation.isPending ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {mutation.isPending ? t('common.loading') : t('student.tickets.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Ticket Details Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-background/80  animate-in fade-in duration-300"
            onClick={() => setSelectedTicket(null)}
          />

          <div className="relative w-full max-w-2xl rounded-[2rem] border border-border bg-card p-2 shadow-2xl shadow-primary/10 animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-background rounded-[1.6rem] p-6 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="space-y-4 pr-12 rtl:pr-0 rtl:pl-12 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${STATUS_CONFIG[selectedTicket.status]?.bg || 'bg-muted'} ${STATUS_CONFIG[selectedTicket.status]?.text || 'text-muted-foreground'}`}
                    >
                      {t(`student.tickets.statuses.${selectedTicket.status}`)}
                    </span>
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted/50 text-xs font-semibold text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                      {format(new Date(selectedTicket.createdAt), 'PP p', {
                        locale: t('common.lang') === 'ar' ? ar : undefined,
                      })}
                    </span>
                  </div>
                  <h2 className="text-xl font-black tracking-tight leading-snug">
                    {selectedTicket.subject}
                  </h2>
                </div>
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="p-2 absolute top-6 right-6 rtl:right-auto rtl:left-6 rounded-xl hover:bg-muted transition-all text-muted-foreground hover:text-foreground shrink-0"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-5 rounded-2xl bg-muted/30 border-2 border-muted/50">
                <p className="text-sm font-medium leading-relaxed text-foreground whitespace-pre-wrap">
                  {selectedTicket.description}
                </p>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="px-8 py-3.5 rounded-2xl text-sm font-black bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-all active:scale-95"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketsPage;
